{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "geo",
  "x-cli-editable": true,
  "x-feature": true,
  "x-tier": "advanced",
  "title": "GEO Content",
  "description": "AI-generated FAQ pages from visitor questions for search engine visibility.",
  "type": "object",
  "properties": {
    "scheduled_generation": {
      "ui:sectionTitle": "Generation",
      "ui:sectionDescription": "How often and how much GEO content is generated.",
      "ui:sectionIcon": "Sparkles",
      "type": "boolean",
      "x-access": "staff",
      "ui:description": "Let the scheduled runner regenerate and republish this domain's FAQ page on its own. Off means the page only changes when someone runs the pipeline by hand \u2014 turning the GEO feature on is enough to generate a page and review it in the backoffice.",
      "default": false
    },
    "generation_cadence_days": {
      "type": "integer",
      "x-access": "staff",
      "ui:description": "How often (in days) the scheduled runner regenerates FAQ content. Only applies when scheduled generation is on.",
      "default": 7,
      "minimum": 1,
      "maximum": 90
    },
    "min_questions_threshold": {
      "type": "integer",
      "x-access": "staff",
      "ui:description": "Minimum number of visitor questions required before the AI can generate FAQ content. If there aren't enough questions, the generation is skipped.",
      "default": 20,
      "minimum": 5,
      "maximum": 500
    },
    "lookback_days": {
      "type": "integer",
      "x-access": "staff",
      "ui:description": "How many days of conversation history to analyze when gathering visitor questions.",
      "default": 30,
      "minimum": 7,
      "maximum": 365
    },
    "top_pages_count": {
      "type": "integer",
      "x-access": "staff",
      "ui:description": "Number of most-visited pages to generate FAQ content for, ranked by question volume.",
      "default": 5,
      "minimum": 1,
      "maximum": 50
    },
    "max_questions": {
      "type": "integer",
      "x-access": "staff",
      "ui:description": "Maximum number of visitor questions to fetch from conversation history per pipeline run. Higher values improve coverage for busy domains but increase processing time.",
      "default": 1000,
      "minimum": 100,
      "maximum": 10000
    },
    "faq_generation_instructions": {
      "ui:sectionTitle": "FAQ Content",
      "ui:sectionDescription": "Instructions that shape the generated FAQs.",
      "ui:sectionIcon": "FileText",
      "type": "string",
      "ui:widget": "textarea",
      "ui:description": "Custom instructions appended to the FAQ generation prompt. Use this to control tone, style, or content rules. Example: 'Always mention that we offer a 14-day free trial. Use formal language. Never mention competitor names.'",
      "maxLength": 2000
    },
    "page_languages": {
      "type": "array",
      "uniqueItems": true,
      "items": {
        "type": "string",
        "enum": ["en", "fr", "es", "de", "it", "pt", "bg", "nl", "ro", "hu"]
      },
      "ui:title": "Page languages",
      "ui:description": "Languages to publish an FAQ page in besides the site language, which always has one. Each language gets its own page, answering the same visitor questions in that language, and the pages point search engines to each other. Leave empty for one page in the site language. Example: a French site that also serves German visitors adds 'de'. Do not add a language another workspace of the same site already publishes in."
    },
    "long_tail": {
      "type": "object",
      "x-access": "staff",
      "x-tier": "advanced",
      "ui:title": "Long-tail FAQ tuning",
      "ui:description": "Controls for the long-tail (single-occurrence) FAQ track. Long-tail questions are visitor questions too rare to cluster, surfaced via an LLM judge that rewrites them and filters duplicates of the main FAQ list.",
      "properties": {
        "max_global": {
          "type": "integer",
          "ui:title": "Max global long-tail FAQs",
          "ui:description": "Hard cap on long-tail FAQs sourced from the site-wide question pool.",
          "default": 20,
          "minimum": 0,
          "maximum": 100
        },
        "max_page": {
          "type": "integer",
          "ui:title": "Max per-page long-tail FAQs",
          "ui:description": "Hard cap on long-tail FAQs sourced from each per-page question pool. 0 disables per-page long-tail.",
          "default": 0,
          "minimum": 0,
          "maximum": 50
        },
        "dedup_jaccard": {
          "type": "number",
          "ui:title": "Long-tail dedup similarity threshold",
          "ui:description": "Token-Jaccard threshold (0.0-1.0). A long-tail candidate is dropped when its similarity to any existing main FAQ title meets or exceeds this value. Higher = more permissive (more long-tail survives).",
          "default": 0.8,
          "minimum": 0.0,
          "maximum": 1.0
        }
      },
      "default": {
        "max_global": 20,
        "max_page": 0,
        "dedup_jaccard": 0.8
      }
    },
    "quality_review": {
      "type": "object",
      "x-access": "staff",
      "x-tier": "advanced",
      "ui:title": "FAQ quality review",
      "ui:description": "Semantic review of every generated FAQ before publishing. An LLM judge accepts, rewrites (twice at most) or rejects each candidate; rejected candidates are replaced by lower-ranked questions. On by default — turn it off only to reproduce the legacy unreviewed output.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "ui:title": "Enable FAQ quality review",
          "ui:description": "When off, FAQs are published straight from the formatter without semantic review (legacy behaviour).",
          "default": true
        }
      },
      "default": {
        "enabled": true
      }
    },
    "publishing": {
      "type": "object",
      "ui:hidden": true,
      "ui:title": "Page styling",
      "ui:description": "How generated FAQ pages look when published. Edited via the dedicated GEO FAQ page-style editor.",
      "allOf": [
        {
          "not": {
            "required": ["public_host", "public_base"],
            "properties": {
              "public_host": { "type": "string" },
              "public_base": { "type": "string" }
            }
          }
        }
      ],
      "properties": {
        "public_host": {
          "type": ["string", "null"],
          "x-tier": "advanced",
          "ui:title": "Public host (custom subdomain)",
          "ui:description": "Optional custom subdomain where the FAQ page is served (e.g. 'faq.client.com'). When set, the canonical URL becomes https://{public_host}/frequently-asked-questions and the Cloudflare Worker 301-redirects faq.userose.ai requests to it. Leave empty to serve under https://faq.userose.ai/{site_domain}/frequently-asked-questions. Requires a CNAME from {public_host} to faq.userose.ai. Mutually exclusive with public_base. Null clears a value a workspace under a path would otherwise inherit from its host.",
          "pattern": "^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$"
        },
        "public_base": {
          "type": ["string", "null"],
          "x-tier": "advanced",
          "ui:title": "Public base URL (subdirectory hosting)",
          "ui:description": "Optional full URL prefix where the FAQ page is served as a subdirectory of the client's main domain (e.g. 'https://www.client.com/faq'). When set, the canonical URL becomes {public_base}/ and the Cloudflare Worker 301-redirects faq.userose.ai requests to it. Requires the client to run a reverse proxy forwarding {public_base}/* to faq.userose.ai/{site_domain}/*. Must be https, no trailing slash, at most two path segments (e.g. 'https://www.client.com/fr/faq' for a workspace under a path). Mutually exclusive with public_host. Null clears a value a workspace under a path would otherwise inherit from its host.",
          "pattern": "^https://[a-z0-9]([a-z0-9-]*[a-z0-9])?(\\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+(/[a-z0-9-]+){0,2}$"
        },
        "hreflang_region": {
          "type": "string",
          "x-access": "staff",
          "ui:title": "Search region (hreflang)",
          "ui:description": "Optional two-letter country code added to the language of every page of this workspace for search engines (e.g. 'GB' marks the English page as en-GB). Set it when two workspaces of the same site publish in the same language, such as a US and a UK English page: without it search engines cannot tell them apart, and neither page lists the other.",
          "pattern": "^[A-Z]{2}$"
        },
        "logo_url": {
          "type": "string",
          "ui:title": "Logo URL",
          "ui:description": "Public URL of the logo to show in the page header. Leave empty to omit."
        },
        "title": {
          "type": "string",
          "ui:title": "Page title",
          "ui:description": "Optional override of the default 'Frequently Asked Questions — {domain}' title."
        },
        "subtitle": {
          "type": "string",
          "ui:title": "Page subtitle"
        },
        "long_tail_section_title": {
          "type": "string",
          "ui:title": "Long-tail section title",
          "ui:description": "Heading shown above the rarer, less-clustered visitor questions. Example: 'Curious about…' or 'More questions visitors asked'."
        },
        "long_tail_section_subtitle": {
          "type": "string",
          "ui:title": "Long-tail section subtitle"
        },
        "search_placeholder": {
          "type": "string",
          "ui:title": "Search bar placeholder",
          "ui:description": "Placeholder text inside the in-page search bar that filters FAQs as visitors type."
        },
        "background_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Background color",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "surface_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Surface color",
          "ui:description": "Card background when divider style is 'card'.",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "text_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Text color",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "muted_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Muted text color",
          "ui:description": "Used for subtitle, answers, and footer text.",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "primary_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Accent color",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "border_color": {
          "type": "string",
          "ui:widget": "color",
          "ui:title": "Border color",
          "pattern": "^#[0-9A-Fa-f]{6}$"
        },
        "font_family": {
          "type": "string",
          "ui:title": "Body font family",
          "ui:description": "CSS font-family for body text."
        },
        "heading_font_family": {
          "type": "string",
          "ui:title": "Heading font family",
          "ui:description": "CSS font-family for the page title and questions."
        },
        "heading_weight": {
          "type": "integer",
          "ui:title": "Heading weight",
          "minimum": 100,
          "maximum": 900
        },
        "border_radius_px": {
          "type": "integer",
          "ui:title": "Border radius (px)",
          "minimum": 0,
          "maximum": 48
        },
        "question_size_px": {
          "type": "integer",
          "ui:title": "Question size (px)",
          "minimum": 10,
          "maximum": 48
        },
        "answer_size_px": {
          "type": "integer",
          "ui:title": "Answer size (px)",
          "minimum": 10,
          "maximum": 36
        },
        "title_size_px": {
          "type": "integer",
          "ui:title": "Title size (px)",
          "minimum": 16,
          "maximum": 128
        },
        "max_width_px": {
          "type": "integer",
          "ui:title": "Max content width (px)",
          "minimum": 320,
          "maximum": 1600
        },
        "divider_style": {
          "type": "string",
          "enum": ["hairline", "rule", "card", "thick"],
          "ui:title": "Divider style"
        },
        "theme_preset": {
          "type": "string",
          "enum": ["brand", "minimal", "editorial", "corporate", "dark", "warm", "bold"],
          "ui:hidden": true,
          "ui:description": "Tracks which preset the page styling was last derived from. Hint only — renderer ignores this field. 'brand' means the styling was auto-derived from appearance.brand_color."
        },
        "custom_css": {
          "type": "string",
          "ui:widget": "textarea",
          "ui:title": "Custom CSS",
          "ui:description": "Raw CSS appended to the page. Scoped to .geo-faq-page at render time.",
          "maxLength": 20000
        }
      },
      "default": {
        "background_color": "#ffffff",
        "surface_color": "#ffffff",
        "text_color": "#111111",
        "muted_color": "#6b7280",
        "primary_color": "#111111",
        "border_color": "#e5e7eb",
        "font_family": "\"Geist\", -apple-system, BlinkMacSystemFont, sans-serif",
        "heading_font_family": "\"Geist\", -apple-system, BlinkMacSystemFont, sans-serif",
        "heading_weight": 600,
        "border_radius_px": 6,
        "question_size_px": 17,
        "answer_size_px": 15,
        "title_size_px": 40,
        "max_width_px": 720,
        "divider_style": "hairline",
        "theme_preset": "minimal"
      }
    }
  },
  "default": {
    "scheduled_generation": false,
    "generation_cadence_days": 7,
    "min_questions_threshold": 20,
    "lookback_days": 30,
    "top_pages_count": 5,
    "max_questions": 1000,
    "long_tail": {
      "max_global": 20,
      "max_page": 0,
      "dedup_jaccard": 0.8
    },
    "quality_review": {
      "enabled": true
    },
    "publishing": {
      "background_color": "#ffffff",
      "surface_color": "#ffffff",
      "text_color": "#111111",
      "muted_color": "#6b7280",
      "primary_color": "#111111",
      "border_color": "#e5e7eb",
      "font_family": "\"Geist\", -apple-system, BlinkMacSystemFont, sans-serif",
      "heading_font_family": "\"Geist\", -apple-system, BlinkMacSystemFont, sans-serif",
      "heading_weight": 600,
      "border_radius_px": 6,
      "question_size_px": 17,
      "answer_size_px": 15,
      "title_size_px": 40,
      "max_width_px": 720,
      "divider_style": "hairline",
      "theme_preset": "minimal"
    }
  }
}
